/**
 * Load Modal Component
 * Main dialog for browsing and loading saved charts
 */

import { devError } from '../../../utils/devLog';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Input,
  Select,
  Text,
  Alert,
  AlertIcon,
  Spinner
} from '@chakra-ui/react';
import { ChartMetadata } from '../../../models/storage';
import { getStorageService } from '../../../services/StorageService';
import { emitChartDeleted, emitChartRenamed } from '../../../hooks/useRecentFiles';
import { useMultipleCharts } from '../../common/AstrologyChart/MultipleChartsContext';
import { globalEventStream } from '../../common/AstrologyChart/universal_architecture/core/EventStream';
import FileList from './FileList';

interface LoadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoadChart: (id: string, metadata: ChartMetadata) => void;
}

/**
 * Modal component for loading saved charts
 */
const LoadModal: React.FC<LoadModalProps> = ({ isOpen, onClose, onLoadChart }) => {
  const [charts, setCharts] = useState<ChartMetadata[]>([]);
  const [filteredCharts, setFilteredCharts] = useState<ChartMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get loaded charts for display name and active chart
  const { charts: loadedCharts, activeChartId } = useMultipleCharts();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'created' | 'modified' | 'accessed'>('modified');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedChart, setSelectedChart] = useState<ChartMetadata | null>(null);
  const [operationLoading, setOperationLoading] = useState(false);

  // Load charts when modal opens
  const loadCharts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const storageService = getStorageService();
      if (!storageService.isInitialized()) {
        setError('Storage service not available');
        return;
      }

      const result = await storageService.listCharts({
        sortBy,
        sortOrder
      });

      if (result.success && result.data) {
        // Use charts directly with metadata.name as single source
        setCharts(result.data);
        // Debug: Loaded charts (production: logs disabled)
      } else {
        setError(result.error || 'Failed to load charts');
      }
    } catch (err) {
      devError('Error loading charts:', err);
      setError('Failed to load charts');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  useEffect(() => {
    if (isOpen) {
      loadCharts();
    }
  }, [isOpen, loadCharts]);
  
  // Listen for chart field renames ONLY when modal is open
  // NOTE: chart.data.updated subscription removed - too broad, caused unnecessary refreshes
  useEffect(() => {
    if (!isOpen) return; // Don't subscribe when closed

    const subscription = globalEventStream.subscribe('chart.field.renamed', () => {
      loadCharts();
    });

    return () => subscription.unsubscribe();
  }, [isOpen, loadCharts]);

  // Get display name from loaded chart (chart_name) or fallback to metadata name
  const getDisplayName = useCallback((metadata: ChartMetadata): string => {
    const loadedChart = loadedCharts.get(metadata.id);
    return loadedChart?.apiResponse?.basic_info?.chart_name || metadata.name;
  }, [loadedCharts]);

  // Get display location from loaded chart (place_name) or fallback to metadata location
  const getDisplayLocation = useCallback((metadata: ChartMetadata): string | undefined => {
    const loadedChart = loadedCharts.get(metadata.id);
    return loadedChart?.apiResponse?.basic_info?.place_name || metadata.chartDateTime?.location;
  }, [loadedCharts]);

  // Filter and sort charts when search or sort options change
  const filterAndSortCharts = useCallback(() => {
    let filtered = [...charts];

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(chart => 
        getDisplayName(chart).toLowerCase().includes(search) ||
        (chart.description && chart.description.toLowerCase().includes(search)) ||
        (chart.chartDateTime?.location && chart.chartDateTime.location.toLowerCase().includes(search))
      );
    }

    // Apply sorting - deleted charts go to bottom
    filtered.sort((a, b) => {
      // First, sort by deletion status - deleted charts go to bottom
      const aDeleted = a.isDeleted || false;
      const bDeleted = b.isDeleted || false;

      if (aDeleted !== bDeleted) {
        return aDeleted ? 1 : -1; // Deleted items go to bottom
      }

      // If both have same deletion status, sort by the selected criteria
      let aVal: any = sortBy === 'name' ? getDisplayName(a) : a[sortBy];
      let bVal: any = sortBy === 'name' ? getDisplayName(b) : b[sortBy];

      if (aVal instanceof Date) aVal = aVal.getTime();
      if (bVal instanceof Date) bVal = bVal.getTime();

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredCharts(filtered);
  }, [charts, searchTerm, sortBy, sortOrder, getDisplayName]);

  useEffect(() => {
    filterAndSortCharts();
  }, [filterAndSortCharts]);

  const handleChartSelect = (chart: ChartMetadata) => {
    setSelectedChart(chart);
  };

  const handleLoadChart = async (chart?: ChartMetadata) => {
    const chartToLoad = chart || selectedChart;
    if (!chartToLoad) return;

    try {
      setOperationLoading(true);
      await onLoadChart(chartToLoad.id, chartToLoad);
      onClose();
    } catch (error) {
      devError('Error loading chart:', error);
      setError('Failed to load chart');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleLoadButtonClick = () => {
    handleLoadChart();
  };

  const handleRenameChart = async (id: string, newName: string) => {
    try {
      setOperationLoading(true);
      
      const storageService = getStorageService();
      const loadResult = await storageService.loadChart(id);
      
      if (loadResult.success && loadResult.data) {
        // Delete old chart and save with new name
        await storageService.deleteChart(id);
        const saveResult = await storageService.saveChart(newName, loadResult.data.chartData);
        
        if (saveResult.success) {
          await loadCharts(); // Reload the list
          emitChartRenamed(saveResult.data!, newName, loadResult.data.metadata.name); // Emit rename event
        } else {
          throw new Error(saveResult.error || 'Failed to save renamed chart');
        }
      } else {
        throw new Error(loadResult.error || 'Failed to load chart for renaming');
      }
    } catch (error) {
      devError('Error renaming chart:', error);
      setError(`Failed to rename chart: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleDeleteChart = async (id: string, name: string) => {
    try {
      setOperationLoading(true);
      
      const storageService = getStorageService();
      
      // Mark chart as deleted (soft delete)
      const now = new Date();
      const permanentDeleteAt = new Date(now.getTime() + 10 * 60 * 1000); // 10 minutes
      
      const result = await storageService.markChartAsDeleted(id, now, permanentDeleteAt);
      
      if (result.success) {
        // Debug: Chart marked for deletion (production: logs disabled)
        
        // Clear selection if deleted chart was selected
        if (selectedChart && selectedChart.id === id) {
          setSelectedChart(null);
        }

        await loadCharts(); // Reload the list
        emitChartDeleted(id, name); // Emit delete event
        
        // Show success message with undo option
        setError(`Chart "${name}" will be permanently deleted in 10 minutes. This change will be reflected after refreshing.`);
      } else {
        throw new Error(result.error || 'Failed to mark chart for deletion');
      }
    } catch (error) {
      devError('Error marking chart for deletion:', error);
      setError(`Failed to delete chart: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleUndoDelete = async (id: string) => {
    try {
      setOperationLoading(true);
      
      const storageService = getStorageService();
      const result = await storageService.undoDeleteChart(id);
      
      if (result.success) {
        // Debug: Chart deletion undone successfully (production: logs disabled)
        await loadCharts(); // Reload the list
        // No need to emit event here - chart is back to normal state
        setError(null); // Clear any previous error messages
      } else {
        throw new Error(result.error || 'Failed to undo chart deletion');
      }
    } catch (error) {
      devError('Error undoing chart deletion:', error);
      setError(`Failed to undo deletion: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedChart(null);
    setSearchTerm('');
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl">
      <ModalOverlay />
      <ModalContent maxWidth="800px">
        <ModalHeader>Load Chart</ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Search and Sort Controls */}
            <HStack spacing={4}>
              <Input
                placeholder="Search charts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                width="440px"
              />
              
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                size="sm"
                width="140px"
              >
                <option value="modified">Last Modified</option>
                <option value="created">Date Created</option>
                <option value="accessed">Last Accessed</option>
                <option value="name">Name</option>
              </Select>
              
              <Select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as any)}
                size="sm"
                width="140px"
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </Select>
            </HStack>

            {/* Error Display */}
            {error && (
              <Alert status="error" size="sm">
                <AlertIcon />
                <Text fontSize="sm">{error}</Text>
              </Alert>
            )}

            {/* Loading State */}
            {loading && (
              <HStack justify="center" py={8}>
                <Spinner />
                <Text>Loading charts...</Text>
              </HStack>
            )}

            {/* Empty State */}
            {!loading && !error && filteredCharts.length === 0 && (
              <VStack py={8} spacing={2}>
                <Text color="gray.500">
                  {searchTerm ? 'No charts match your search' : 'No saved charts found'}
                </Text>
                {searchTerm && (
                  <Button size="sm" variant="outline" onClick={() => setSearchTerm('')}>
                    Clear Search
                  </Button>
                )}
              </VStack>
            )}

            {/* Charts List */}
            {!loading && filteredCharts.length > 0 && (
              <FileList
                charts={filteredCharts}
                selectedChart={selectedChart}
                onChartSelect={handleChartSelect}
                onChartDoubleClick={handleLoadChart}
                onRenameChart={handleRenameChart}
                onDeleteChart={handleDeleteChart}
                onUndoDelete={handleUndoDelete}
                operationLoading={operationLoading}
                getDisplayName={getDisplayName}
                getDisplayLocation={getDisplayLocation}
                activeChartId={activeChartId || undefined}
              />
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleLoadButtonClick}
            isLoading={operationLoading}
            loadingText="Loading..."
            disabled={!selectedChart || operationLoading}
          >
            Load Chart
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LoadModal;
